import { CONFIG } from '../config/constants.js';
import { BUILDINGS } from '../config/buildings.js';

/**
 * Updates building progress and checks for completion
 * @param {Array} buildings - Array of building objects
 * @param {Object|null} buildingInProgress - Current building being constructed
 * @param {number} progressGained - Progress to add
 * @returns {Object} { buildings, buildingInProgress }
 */
export function updateBuildingProgress(buildings, buildingInProgress, progressGained) {
  if (!buildingInProgress || progressGained === 0) {
    return { buildings, buildingInProgress };
  }

  const newBuildings = buildings.map(b => {
    if (b.type === buildingInProgress.type && !b.complete) {
      const newProgress = b.progress + progressGained;
      const buildingDef = BUILDINGS[b.type];
      const threshold = buildingDef.buildThreshold || CONFIG.BUILD_THRESHOLD;

      return {
        ...b,
        progress: newProgress,
        complete: newProgress >= threshold
      };
    }
    return b;
  });

  // Check if there's still an incomplete building
  const incompleteBuilding = newBuildings.find(b => !b.complete);
  const newBuildingInProgress = incompleteBuilding
    ? { type: incompleteBuilding.type, progress: incompleteBuilding.progress }
    : null;

  return {
    buildings: newBuildings,
    buildingInProgress: newBuildingInProgress
  };
}

/**
 * Starts a new building project
 * @param {string} type - Building type
 * @param {Object} currentResources - Current resources { food, wood, stone, metal }
 * @param {Array} buildings - Existing buildings
 * @returns {Object|null} { resources, buildingInProgress, building } or null if can't afford
 */
export function startBuilding(type, currentResources, buildings, discountFactor = 0) {
  const buildingDef = BUILDINGS[type];

  if (!buildingDef) return null;

  // Apply discount (e.g. 0.2 = 20% off from Skilled Artisan story event)
  const factor = 1 - discountFactor;
  const woodCost = Math.ceil(buildingDef.wood * factor);
  const stoneCost = Math.ceil(buildingDef.stone * factor);
  const metalCost = Math.ceil((buildingDef.metal || 0) * factor);

  if (
    currentResources.wood < woodCost ||
    currentResources.stone < stoneCost ||
    (currentResources.metal || 0) < metalCost
  ) {
    return null;
  }

  // Check if building already exists
  if (buildings.some(b => b.type === type)) {
    return null;
  }

  return {
    resources: {
      food: currentResources.food,
      wood: currentResources.wood - woodCost,
      stone: currentResources.stone - stoneCost,
      metal: (currentResources.metal || 0) - metalCost
    },
    buildingInProgress: { type, progress: 0 },
    building: { type, progress: 0, complete: false }
  };
}

/**
 * Checks if a building can be afforded
 * @param {string} type - Building type
 * @param {Object} resources - Current resources
 * @returns {boolean} True if affordable
 */
export function canAffordBuilding(type, resources) {
  const buildingDef = BUILDINGS[type];
  if (!buildingDef) return false;

  const metalCost = buildingDef.metal || 0;
  return (
    resources.wood >= buildingDef.wood &&
    resources.stone >= buildingDef.stone &&
    (resources.metal || 0) >= metalCost
  );
}

/**
 * Checks if a building meets its unlock conditions
 * @param {string} type - Building type
 * @param {Object} gameState - Current game state
 * @returns {Object} { unlocked: boolean, reason: string }
 */
export function canUnlockBuilding(type, gameState) {
  const buildingDef = BUILDINGS[type];
  if (!buildingDef) return { unlocked: false, reason: 'Unknown building' };

  // If no unlock conditions, it's always unlocked
  if (!buildingDef.unlockConditions) {
    return { unlocked: true, reason: '' };
  }

  const conditions = buildingDef.unlockConditions;

  // Check minimum day
  if (conditions.minDay && gameState.day < conditions.minDay) {
    return { unlocked: false, reason: `Unlocks on day ${conditions.minDay}` };
  }

  // Check required buildings
  if (conditions.requiredBuildings) {
    const missingBuildings = conditions.requiredBuildings.filter(
      reqType => !gameState.buildings.some(b => b.type === reqType && b.complete)
    );
    if (missingBuildings.length > 0) {
      return {
        unlocked: false,
        reason: `Requires: ${missingBuildings.map(t => BUILDINGS[t].name).join(', ')}`
      };
    }
  }

  // Check required tech points
  if (conditions.requiredTech && (gameState.techPoints || 0) < conditions.requiredTech) {
    return {
      unlocked: false,
      reason: `Requires ${conditions.requiredTech} tech points`
    };
  }

  // Check minimum knowledge
  if (conditions.minKnowledge && (gameState.knowledge || 0) < conditions.minKnowledge) {
    return {
      unlocked: false,
      reason: `Requires ${conditions.minKnowledge} knowledge`
    };
  }

  // Check minimum morale
  if (conditions.minMorale && (gameState.morale || 50) < conditions.minMorale) {
    return {
      unlocked: false,
      reason: `Requires ${conditions.minMorale} morale`
    };
  }

  // Check minimum population
  if (conditions.minPopulation) {
    const aliveCount = gameState.workers.filter(w => w.status !== 'dead').length;
    if (aliveCount < conditions.minPopulation) {
      return {
        unlocked: false,
        reason: `Requires ${conditions.minPopulation} living workers`
      };
    }
  }

  // Check raids defeated
  if (conditions.raidsDefeated && (gameState.raidsDefeated || 0) < conditions.raidsDefeated) {
    return {
      unlocked: false,
      reason: `Requires ${conditions.raidsDefeated} raids defeated`
    };
  }

  return { unlocked: true, reason: '' };
}
